/* ============================================================
   Section: combo-steps  (templates/product.combo-pdp.json)
   ------------------------------------------------------------
   WAI-ARIA tabs for the "One routine. Two roles." panels.
   Markup is rendered by Liquid — this only moves selection
   state and focus. Panels are shown/hidden with the semantic
   `hidden` attribute, never inline styles.
   ============================================================ */

class ComboSteps extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('[data-combo-steps-tablist]');
    if (!this.tablist) return;

    this.tabs = [...this.querySelectorAll('[data-combo-steps-tab]')];
    this.panels = [...this.querySelectorAll('[data-combo-steps-panel]')];
    if (this.tabs.length < 2) return;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.tablist.addEventListener('click', this.handleClick);
    this.tablist.addEventListener('keydown', this.handleKeydown);

    const initial = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.select(initial < 0 ? 0 : initial, { focus: false });
  }

  disconnectedCallback() {
    this.tablist?.removeEventListener('click', this.handleClick);
    this.tablist?.removeEventListener('keydown', this.handleKeydown);
  }

  handleClick(event) {
    const tab = event.target.closest('[data-combo-steps-tab]');
    if (!tab) return;

    this.select(this.tabs.indexOf(tab), { focus: false });
  }

  handleKeydown(event) {
    const { key } = event;
    const currentIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const lastIndex = this.tabs.length - 1;
    let nextIndex = null;

    if (key === 'ArrowRight') nextIndex = currentIndex >= lastIndex ? 0 : currentIndex + 1;
    else if (key === 'ArrowLeft') nextIndex = currentIndex <= 0 ? lastIndex : currentIndex - 1;
    else if (key === 'Home') nextIndex = 0;
    else if (key === 'End') nextIndex = lastIndex;

    if (nextIndex === null) return;

    event.preventDefault();
    this.select(nextIndex, { focus: true });
  }

  select(index, { focus }) {
    for (const [position, tab] of this.tabs.entries()) {
      const isActive = position === index;
      tab.setAttribute('aria-selected', String(isActive));
      /* Roving tabindex: only the selected tab is in the tab order. */
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [position, panel] of this.panels.entries()) {
      panel.toggleAttribute('hidden', position !== index);
    }

    if (focus) this.tabs[index].focus();
  }
}

if (!customElements.get('combo-steps')) {
  customElements.define('combo-steps', ComboSteps);
}
